/**
 * Utility for parsing query parameters from the SDK script URL.
 * Used to configure SDK behavior via script tag attributes.
 */

let capturedBaseUri: string | null = null
let baseUriOverride: string | null = null

const SDK_PATH_MARKER = '/api/checkout/sdk/js/'

// Legacy standalone embedded-app origin. Hardcoded and kept trusted during
// the monolith migration so SDKs still served from it aren't rejected.
// Remove once the migration is stable: https://gofundme.atlassian.net/browse/PRO-6540
const LEGACY_EMBEDDED_APP_URI = 'https://giving.classy.org/embedded'

function normalizeBaseUri(baseUri?: string | null): string | null {
  if (!baseUri) {
    return null
  }

  try {
    const url = new URL(baseUri)
    const normalizedPath = url.pathname.replace(/\/$/, '')
    return `${url.origin}${normalizedPath}`
  } catch {
    return null
  }
}

function getTrustedBaseUris(): string[] {
  return [
    process.env.EMBEDDED_APP_URI,
    process.env.EMBEDDED_APP_URI_GFM,
    LEGACY_EMBEDDED_APP_URI,
  ].filter((baseUri): baseUri is string => Boolean(baseUri))
}

function resolveTrustedBaseUri(baseUri?: string | null): string | null {
  const normalizedCandidate = normalizeBaseUri(baseUri)

  if (!normalizedCandidate) {
    return null
  }

  const matchingBaseUri = getTrustedBaseUris().find(
    (trustedBaseUri) => normalizeBaseUri(trustedBaseUri) === normalizedCandidate
  )

  return matchingBaseUri || null
}

function resolveBaseUriFromScriptSrc(scriptSrc: string): string | null {
  if (!scriptSrc) {
    return null
  }

  try {
    const url = new URL(scriptSrc)
    const markerIndex = url.pathname.indexOf(SDK_PATH_MARKER)

    if (markerIndex === -1) {
      return null
    }

    const basePath = url.pathname.slice(0, markerIndex)
    return resolveTrustedBaseUri(`${url.origin}${basePath}`)
  } catch {
    return null
  }
}

/**
 * Parses a script URL and checks if manual initialization is requested.
 * Accepts 'true', '1', or empty string as truthy values for manual-init param.
 *
 * @param scriptSrc - The src attribute of the script element
 * @returns true if manual-init param indicates manual initialization should be used
 *
 * @example
 * ```typescript
 * parseManualInitParam('https://embed.classy.org/api/checkout/sdk/js/123?manual-init=true') // true
 * parseManualInitParam('https://embed.classy.org/api/checkout/sdk/js/123?manual-init=1') // true
 * parseManualInitParam('https://embed.classy.org/api/checkout/sdk/js/123?manual-init') // true
 * parseManualInitParam('https://embed.classy.org/api/checkout/sdk/js/123') // false
 * parseManualInitParam('https://embed.classy.org/api/checkout/sdk/js/123?manual-init=false') // false
 * ```
 */
export function parseManualInitParam(scriptSrc: string): boolean {
  if (!scriptSrc) {
    return false
  }

  try {
    const url = new URL(scriptSrc)
    const manualInit = url.searchParams.get('manual-init')
    // Accept 'true', '1', or just presence of the param with no value
    return manualInit === 'true' || manualInit === '1' || manualInit === ''
  } catch {
    // Invalid URL, ignore
    return false
  }
}

/**
 * Captures the trusted embedded app base URI from the current SDK script tag.
 * When a script src is provided explicitly, it is used instead of document.currentScript.
 */
export function captureBaseUri(scriptSrc?: string | null): string | null {
  const resolvedScriptSrc =
    typeof scriptSrc === 'string'
      ? scriptSrc
      : typeof document !== 'undefined' && document.currentScript
      ? (document.currentScript as HTMLScriptElement).src
      : ''

  capturedBaseUri = resolveBaseUriFromScriptSrc(resolvedScriptSrc)
  return capturedBaseUri
}

/**
 * Sets an optional trusted runtime override for SPA/manual-init consumers.
 * Invalid values are ignored and cause the SDK to fall back to the captured/default URI.
 */
export function setBaseUri(baseUri?: string | null): void {
  baseUriOverride = resolveTrustedBaseUri(baseUri)
}

/**
 * Returns the runtime embedded app base URI.
 * Priority: manual override -> captured script base URI -> default EMBEDDED_APP_URI.
 */
export function getBaseUri(): string {
  return baseUriOverride || capturedBaseUri || process.env.EMBEDDED_APP_URI
}

/**
 * Returns the iframe (embedded-app) base URI.
 * Always the canonical app URI — never the captured SDK-script origin — so the
 * iframe stays on the donate route (e.g. /donate/embedded) even when the SDK
 * itself is served from a legacy /embedded origin. The legacy origin is only a
 * valid place to load the SDK script from, never to load the iframe from.
 * Priority: manual override -> default EMBEDDED_APP_URI.
 */
export function getIframeBaseUri(): string {
  return baseUriOverride || process.env.EMBEDDED_APP_URI
}

export type IframeRouteReason = 'override' | 'default'
export type SdkScriptOrigin = 'legacy' | 'monolith' | 'gfm' | 'none'

/**
 * Reports how getIframeBaseUri() chose the iframe base URI:
 * a runtime override from `eg.init({ baseUri })` or the build-time default.
 */
export function getIframeRouteReason(): IframeRouteReason {
  return baseUriOverride ? 'override' : 'default'
}

/**
 * Classifies which trusted origin served the SDK script itself, from the
 * captured script src. 'none' means the script was loaded from a URL the SDK
 * does not recognize (self-hosted copy, direct CDN URL, or untrusted origin).
 */
export function getSdkScriptOrigin(): SdkScriptOrigin {
  if (!capturedBaseUri) {
    return 'none'
  }
  if (capturedBaseUri === LEGACY_EMBEDDED_APP_URI) {
    return 'legacy'
  }
  if (capturedBaseUri === process.env.EMBEDDED_APP_URI) {
    return 'monolith'
  }
  if (capturedBaseUri === process.env.EMBEDDED_APP_URI_GFM) {
    return 'gfm'
  }
  return 'none'
}

/**
 * Checks if the SDK should skip automatic initialization by looking for
 * the `manual-init` query parameter on the current script's URL.
 *
 * Uses document.currentScript which is only available during script execution.
 *
 * @returns true if auto-initialization should be skipped
 *
 * @example
 * ```html
 * <script async src="https://embed.classy.org/api/checkout/sdk/js/YOUR_ORG_ID?manual-init=true"></script>
 * ```
 */
export function shouldSkipAutoInit(): boolean {
  if (typeof document !== 'undefined' && document.currentScript) {
    const scriptSrc = (document.currentScript as HTMLScriptElement).src
    return parseManualInitParam(scriptSrc)
  }
  return false
}
